// ============================================================================
// PHOTO PERSISTENCE HELPERS
// ----------------------------------------------------------------------------
// Keeps project/quote media portable. Browser-local blobId references are fast
// for the active session, but they are not enough for saved quotes, restored
// projects, PDFs, or another browser profile. These helpers upload when the
// backend is available, merge durable media fields back into scope assignments,
// and only strip inline data when a durable backing source exists.
// ============================================================================

import { isFileUploadAuthError, isFileUploadDeferralError, uploadFileToS3 } from './file-upload-service';
import {
    CLOUD_MEDIA_CLASSES,
    CLOUD_MEDIA_KINDS,
    decideCloudMediaUpload,
} from './cloud-media-policy';
import { dataUrlToBlob } from '../utils/data-url-to-blob';
import { queuePhotoCaption } from './photo-caption-queue';

let lastDeferredUploadLogAt = 0;
let lastAuthSkippedUploadLogAt = 0;
const DEFERRED_UPLOAD_LOG_INTERVAL_MS = 15000;
const AUTH_SKIPPED_UPLOAD_LOG_INTERVAL_MS = 15000;

// CZ-C (PRICING-TRACE 2026-05-15): pre-strip backend-resource validation.
// The architect's investigation found the partial-photo-loss class-of-bug:
// `normalisePhotoForPersistence` strips inline `data` the moment `s3Key` is
// set. If the backend roundtrip later fails (file_metadata row orphaned,
// signed URL expired, backend unreachable) the photo renders blank with no
// recoverable bytes. The fix is to validate the backend resource is
// reachable BEFORE stripping inline data — if validation fails, keep the
// inline data and tag the photo with `_pendingValidation: true` so the UI
// can show a "needs recovery" badge.
//
// Kill switch: `localStorage.qm_disable_photo_pre_strip_validation === '1'`
// reverts to legacy unconditional strip.
//
// Performance: validation runs in parallel across the photo array; per-s3Key
// results cached in-memory for the session so a 30-photo save doesn't fire
// 30 HEAD requests every time the user touches the project.
const _backendValidationCache = new Map();           // s3Key → 'ok' | 'fail'
const _BACKEND_VALIDATION_TIMEOUT_MS = 2500;

function _preStripValidationKillSwitch() {
    try {
        return typeof localStorage !== 'undefined'
            && localStorage.getItem('qm_disable_photo_pre_strip_validation') === '1';
    } catch { return false; }
}

async function _validateBackendResource(photo) {
    const key = photo?.s3Key;
    if (!key) return 'no_key';
    const cached = _backendValidationCache.get(key);
    if (cached) return cached;
    // No-validate path: when the file_metadata row is set we trust it (cheaper
    // than re-HEAD-ing the signed URL for every save). The signed URL itself
    // may expire but file_metadata is the canonical durable pointer.
    if (photo.fileMetadataId) {
        _backendValidationCache.set(key, 'ok');
        return 'ok';
    }
    // Best-effort HEAD against the signed URL if one is available on the
    // photo record. If no HEAD-target exists, treat as 'ok' (we can't prove
    // failure → don't lose data by being overly cautious).
    const url = photo.previewUrl || photo.signedUrl || photo.url;
    if (!url || typeof url !== 'string') {
        _backendValidationCache.set(key, 'ok');
        return 'ok';
    }
    try {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = controller ? setTimeout(() => controller.abort(), _BACKEND_VALIDATION_TIMEOUT_MS) : null;
        const response = await fetch(url, { method: 'HEAD', signal: controller?.signal });
        if (timeoutId) clearTimeout(timeoutId);
        const ok = response && response.ok;
        _backendValidationCache.set(key, ok ? 'ok' : 'fail');
        return ok ? 'ok' : 'fail';
    } catch {
        // Network error / timeout / CORS / etc. — be conservative: don't
        // cache as 'fail' (might be transient), but treat THIS save as 'fail'
        // so the strip is skipped.
        return 'fail';
    }
}

function logDeferredUploadOnce(err) {
    const now = Date.now();
    if (now - lastDeferredUploadLogAt < DEFERRED_UPLOAD_LOG_INTERVAL_MS) return;
    lastDeferredUploadLogAt = now;
    const seconds = err?.retryAfterSeconds || Math.ceil((err?.retryAfterMs || 0) / 1000) || 1;
    console.info(`[photo-persistence] Photo uploads deferred for ${seconds}s after backend rate limiting; project save will continue with local references.`);
}

function hasAccessToken() {
    try {
        return typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('qm_access_token');
    } catch {
        return false;
    }
}

function isAuthUploadError(err) {
    const message = String(err?.message || err?.error || '');
    return isFileUploadAuthError(err)
        || err?.status === 401
        || err?.status === 403
        || /authentication required|unauthori[sz]ed|forbidden|jwt|token/i.test(message);
}

function logAuthSkippedUploadOnce() {
    const now = Date.now();
    if (now - lastAuthSkippedUploadLogAt < AUTH_SKIPPED_UPLOAD_LOG_INTERVAL_MS) return;
    lastAuthSkippedUploadLogAt = now;
    console.info('[photo-persistence] Photo cloud upload skipped because the current session is not authenticated; project save will continue with local references.');
}

function isBackendMode() {
    try {
        return import.meta?.env?.VITE_USE_BACKEND !== 'false';
    } catch {
        return false;
    }
}

function isObjectPhoto(photo) {
    return !!photo && typeof photo === 'object' && !Array.isArray(photo);
}

async function enqueuePhotoUploadRetry(photo, projectId, payload, opts = {}, retryAfterMs = null) {
    const { enqueueUpload } = await import('./upload-queue');
    const retryBlob = photo.blobId
        ? null
        : (isDataUrl(payload.data)
            ? await dataUrlToBlob(payload.data, payload.contentType || 'image/jpeg')
            : payload.data);
    const queueId = await enqueueUpload({
        projectId,
        entityType: opts.entityType || (photo.fromVideo ? 'project_video_frame' : 'project_photo'),
        entityId: photo.id,
        ...(photo.blobId ? { blobId: photo.blobId } : { blob: retryBlob }),
        contentType: payload.contentType,
        originalFilename: photo.originalName || photo.name || `${photo.id}.jpg`,
        cloudMedia: photo.cloudMedia,
        ...(Number.isFinite(Number(retryAfterMs)) && Number(retryAfterMs) > 0
            ? { retryAfterMs: Number(retryAfterMs) }
            : {}),
    });
    if (queueId == null) {
        const error = new Error('Photo upload retry could not be persisted.');
        error.code = 'PHOTO_UPLOAD_RETRY_QUEUE_FAILED';
        error.retryable = true;
        throw error;
    }
    return queueId;
}

function isDataUrl(value) {
    return typeof value === 'string' && value.startsWith('data:');
}

function dataUrlContentType(dataUrl) {
    if (!isDataUrl(dataUrl)) return null;
    const match = dataUrl.match(/^data:([^;,]+)/);
    return match?.[1] || null;
}

function cloneWithoutUndefined(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));
}

// ─── DEC-COUNCIL-007 / Candidate C6 Slice 4 — persistence invariant guard ─
// Tsunade's hardening: today the strip-inline-data invariant is enforced by
// CODE PATH (the conditional branches inside normalisePhotoForPersistence).
// If a future refactor — or a caller that bypasses normalisePhotoForPersistence
// — slips inline `data` through to a save, no test catches it. The 2026-05-15
// partial-photo-loss class-of-bug was exactly this shape: pre-strip happened
// before the backend roundtrip was confirmed, leaving blank photos.
//
// This guard runs in dev-only (warn loud) + can be promoted to throw via
// `localStorage.qm_photo_persistence_guard === 'strict'`. Production never
// throws — graceful is better than a missing photo, but the warn surfaces
// the bug for the next dev to read.
//
// Kill switch: `localStorage.qm_disable_photo_persistence_guard === '1'`
// disables both the warn + the strict throw. Use during incident response
// only; not for normal commits.

function _persistenceGuardMode() {
    try {
        if (typeof localStorage === 'undefined') return 'off';
        if (localStorage.getItem('qm_disable_photo_persistence_guard') === '1') return 'off';
        if (localStorage.getItem('qm_photo_persistence_guard') === 'strict') return 'strict';
        // Default: warn in dev, off in production builds. import.meta.env.DEV
        // is set by Vite at build time.
        try { if (import.meta?.env?.DEV === true) return 'warn'; } catch { /* nope */ }
        return 'off';
    } catch {
        return 'off';
    }
}

/**
 * Run the strip-inline-data invariant check. Called at the tail of
 * normalisePhotoForPersistence. Loud in dev / strict, silent in prod.
 *
 * The invariant: a photo about to be persisted MUST NOT carry inline
 * `data` UNLESS one of the four legitimate opt-outs is set:
 *   - `_localBlobOnly: true`     — durable backing source is the local Blob; inline is the only render path
 *   - `_pendingValidation: true` — pre-strip validation failed; inline preserved for recovery
 *   - `fromVideo: true`          — photo derived from a video frame; lifecycle differs
 *   - `_skipBlobMigration: true` — caller explicitly opted out of the migration
 */
function _assertNoStrandedInlineData(photo) {
    const mode = _persistenceGuardMode();
    if (mode === 'off') return;
    if (!isObjectPhoto(photo)) return;
    const hasInline = isDataUrl(photo.data) || isDataUrl(photo.dataUrl);
    if (!hasInline) return;
    const hasOptOut = !!(photo._localBlobOnly || photo._pendingValidation || photo.fromVideo || photo._skipBlobMigration);
    if (hasOptOut) return;
    const name = getPhotoDisplayName(photo, photo.id || photo.blobId || 'unknown');
    const msg = `[photo-persistence] strip-inline-data invariant violation: photo "${name}" has inline data but no legitimate opt-out flag (_localBlobOnly | _pendingValidation | fromVideo | _skipBlobMigration). This will silently drop bytes on the next save. See DEC-COUNCIL-007 Candidate C6 Slice 4.`;
    if (mode === 'strict') {
        const err = new Error(msg);
        err.code = 'PHOTO_PERSISTENCE_INVARIANT';
        throw err;
    }
    console.warn(msg);
}

function hasManualInlineReplacement(photo) {
    return isObjectPhoto(photo)
        && isDataUrl(photo.data)
        && !!(photo._forcePhotoUpload || photo.manualEditedAt);
}

const SUPERSEDED_MANUAL_REPLACEMENT_FIELDS = new Set([
    'blobId',
    's3Key',
    'fileMetadataId',
    'url',
    'thumbnailUrl',
    'uploadedAt',
    'cloudMedia',
]);

export function getPhotoDisplayName(photo, fallback = 'Photo') {
    if (typeof photo === 'string') return fallback;
    if (!isObjectPhoto(photo)) return fallback;
    return photo.name
        || photo.originalName
        || photo.fileName
        || photo.aiLabel
        || (photo.fromVideo && photo.timestamp != null ? `Video frame @ ${photo.timestamp}s` : '')
        || fallback;
}

export function buildPhotoCatalog(photos = []) {
    const byId = new Map();
    const byName = new Map();
    if (!Array.isArray(photos)) return { byId, byName };

    for (const photo of photos) {
        if (!isObjectPhoto(photo)) continue;
        if (photo.id) byId.set(String(photo.id), photo);
        for (const name of [photo.name, photo.originalName, photo.fileName]) {
            if (name) byName.set(String(name), photo);
        }
    }
    return { byId, byName };
}

export function mergePortablePhotoFields(photo, source) {
    if (!isObjectPhoto(photo) || !isObjectPhoto(source)) return photo;
    const next = { ...photo };
    const manualReplacement = hasManualInlineReplacement(photo);
    for (const field of [
        'data',
        'blobId',
        's3Key',
        'fileMetadataId',
        'contentType',
        'url',
        'thumbnailUrl',
        'originalData',
        'aiMarkupData',
        'aiMarkupRegions',
        'aiAnnotated',
        'manualEditedAt',
        'width',
        'height',
        'sizeKB',
        'cloudMedia',
    ]) {
        // A markup replacement deliberately cleared the old object identity.
        // Catalog enrichment must not resurrect it before the replacement
        // bytes have earned their own durable upload receipt.
        if (manualReplacement && SUPERSEDED_MANUAL_REPLACEMENT_FIELDS.has(field)) continue;
        if (next[field] == null && source[field] != null) {
            next[field] = source[field];
        }
    }
    return next;
}

export function findCatalogPhoto(photo, catalog) {
    if (!isObjectPhoto(photo) || !catalog) return null;
    if (photo.id && catalog.byId?.has(String(photo.id))) {
        return catalog.byId.get(String(photo.id));
    }
    for (const name of [photo.name, photo.originalName, photo.fileName]) {
        if (name && catalog.byName?.has(String(name))) {
            return catalog.byName.get(String(name));
        }
    }
    return null;
}

async function resolveUploadPayload(photo, projectId = null) {
    if (!isObjectPhoto(photo)) return null;

    if (isDataUrl(photo.data)) {
        return {
            data: photo.data,
            contentType: dataUrlContentType(photo.data) || photo.contentType || 'image/jpeg',
        };
    }

    if (isDataUrl(photo.dataUrl)) {
        return {
            data: photo.dataUrl,
            contentType: dataUrlContentType(photo.dataUrl) || photo.contentType || 'image/jpeg',
        };
    }

    if (photo.blobId) {
        try {
            const { getPhotoBlob } = await import('./photo-blob-store');
            // v5.46.72 (cross-project photo-bleed fix): name the owning project
            // so the store's integrity guard refuses a foreign blob. photo_blobs
            // ids are GLOBAL Dexie auto-increments — after a local-DB reset an
            // old record's blobId can point at ANOTHER project's bytes, and
            // without this check the S3 repair would upload the wrong project's
            // image under THIS project's key, making the bleed durable.
            const blob = await getPhotoBlob(photo.blobId, projectId);
            if (blob instanceof Blob) {
                return {
                    data: blob,
                    contentType: blob.type || photo.contentType || 'image/jpeg',
                };
            }
        } catch (err) {
            console.warn('[photo-persistence] Blob lookup failed:', err?.message);
        }
    }

    return null;
}

export async function uploadPhotoToS3IfPossible(photo, projectId, opts = {}) {
    const shouldReplaceExisting = hasManualInlineReplacement(photo);
    if (!isBackendMode() || !projectId || !isObjectPhoto(photo) || !photo.id || (photo.s3Key && !shouldReplaceExisting)) {
        return photo;
    }
    // S3 (2026-08-19): the background upload queue may have already uploaded
    // this photo's bytes after an earlier save-time failure and stamped the
    // earned s3Key on the blob row. Adopt it instead of re-uploading the same
    // bytes to the same deterministic key. Ownership rule matches the stamp.
    if (photo.blobId && !shouldReplaceExisting) {
        try {
            const { getPhotoBlobS3Key } = await import('./photo-blob-store');
            const stampedKey = await getPhotoBlobS3Key(photo.blobId, projectId);
            if (stampedKey) {
                const adopted = cloneWithoutUndefined({
                    ...photo,
                    s3Key: stampedKey,
                    uploadedAt: photo.uploadedAt || new Date().toISOString(),
                    _cloudUploadPending: undefined,
                    _cloudUploadPendingReason: undefined,
                    _forcePhotoUpload: undefined,
                });
                // Stored by the retry queue: captioned now, as any upload is.
                queuePhotoCaption(adopted, projectId);
                return adopted;
            }
        } catch { /* fall through to the normal upload */ }
    }

    // Customer-cloud invariant: only an explicitly classified compressed
    // derivative may enter either the immediate upload or persistent retry
    // lane. Local originals stay usable in memory/Dexie but cannot be opted in
    // by a browser flag or caller-provided boolean.
    const authenticated = hasAccessToken();
    const mediaDecision = decideCloudMediaUpload(
        photo.cloudMedia,
        photo.fromVideo ? CLOUD_MEDIA_KINDS.VIDEO_FRAME : CLOUD_MEDIA_KINDS.PHOTO_PREVIEW,
    );
    if (!mediaDecision.allowed) {
        if (!authenticated) {
            logAuthSkippedUploadOnce();
            return photo;
        }
        return cloneWithoutUndefined({
            ...photo,
            _cloudUploadBlocked: true,
            _cloudUploadBlockedReason: mediaDecision.reason,
        });
    }

    const payload = await resolveUploadPayload(photo, projectId);
    if (!payload) return photo;

    if (!authenticated) {
        logAuthSkippedUploadOnce();
        await enqueuePhotoUploadRetry(photo, projectId, payload, opts, 60000);
        return cloneWithoutUndefined({
            ...photo,
            _cloudUploadPending: true,
            _cloudUploadPendingReason: 'authentication_required',
        });
    }

    try {
        const result = await uploadFileToS3({
            data: payload.data,
            projectId,
            entityType: opts.entityType || (photo.fromVideo ? 'project_video_frame' : 'project_photo'),
            entityId: photo.id,
            contentType: payload.contentType,
            originalFilename: photo.originalName || photo.name || `${photo.id}.jpg`,
            cloudMedia: photo.cloudMedia,
            signal: opts.signal,
        });

        if (photo.blobId && result?.s3Key) {
            try {
                const { updatePhotoS3Key } = await import('./photo-blob-store');
                // Same ownership rule as the payload read: never stamp an s3Key
                // onto a blob row this project does not own.
                await updatePhotoS3Key(photo.blobId, result.s3Key, projectId);
            } catch (err) {
                console.warn('[photo-persistence] Failed to stamp blob S3 key:', err?.message);
            }
        }

        const stored = cloneWithoutUndefined({
            ...photo,
            s3Key: result?.s3Key,
            fileMetadataId: result?.fileMetadataId || photo.fileMetadataId,
            contentType: result?.contentType || payload.contentType || photo.contentType,
            cloudMedia: result?.cloudMedia || photo.cloudMedia,
            uploadedAt: new Date().toISOString(),
            _cloudUploadPending: undefined,
            _cloudUploadPendingReason: undefined,
            _forcePhotoUpload: undefined,
        });
        // Captioned on upload (photo-caption-queue.ts); the upload does not wait for it.
        queuePhotoCaption(stored, projectId);
        return stored;
    } catch (err) {
        // A project switch/reset owns this cancellation. Do not turn stale media
        // work into a background upload or let the superseded save continue.
        if (err?.name === 'AbortError' || err?.code === 'file_upload_aborted') {
            throw err;
        }
        if (isFileUploadDeferralError(err)) {
            logDeferredUploadOnce(err);
            await enqueuePhotoUploadRetry(photo, projectId, payload, opts, err?.retryAfterMs);
            return cloneWithoutUndefined({
                ...photo,
                _cloudUploadPending: true,
                _cloudUploadPendingReason: 'rate_limited',
            });
        }
        if (isAuthUploadError(err)) {
            logAuthSkippedUploadOnce();
            await enqueuePhotoUploadRetry(photo, projectId, payload, opts, 60000);
            return cloneWithoutUndefined({
                ...photo,
                _cloudUploadPending: true,
                _cloudUploadPendingReason: 'authentication_required',
            });
        }
        console.warn(`[photo-persistence] S3 upload failed for ${getPhotoDisplayName(photo, photo.id)}:`, err?.message);
        // S3 (2026-08-19): this failure class used to be DROPPED until the
        // next save happened to retry it. Hand it to the persistent upload
        // queue instead (5 retries, exponential backoff, survives reload via
        // resumeUploadQueue). The next save adopts the queue-earned key via
        // getPhotoBlobS3Key.
        try {
            await enqueuePhotoUploadRetry(photo, projectId, payload, opts);
        } catch (queueErr) {
            console.warn('[photo-persistence] Could not queue background retry:', queueErr?.message);
            throw queueErr;
        }
        return photo;
    }
}

function _normalisePhotoForPersistenceImpl(photo) {
    if (!isObjectPhoto(photo)) return photo;

    if (photo.cloudMedia?.mediaClass === CLOUD_MEDIA_CLASSES.LOCAL_ORIGINAL && !photo.s3Key) {
        // Keep camera/source bytes only in React state or the browser-local
        // Blob store. The returned value is the backend project envelope, so
        // every inline representation may be removed only after local Blob
        // persistence succeeded. Otherwise stripping here would turn a
        // recoverable storage failure into a successful metadata-only save.
        const hasInlineSourceBytes = [photo.data, photo.dataUrl, photo.originalData, photo.aiMarkupData]
            .some(value => isDataUrl(value));
        // migrateProjectPhotos removes the inline copy only after an
        // owner-gated blob read/write succeeds. If bytes remain, a present
        // blobId may itself be stale, missing or owned by another Project.
        if (hasInlineSourceBytes) {
            const error = new Error(
                `Local-original photo "${getPhotoDisplayName(photo, photo.id || 'unknown')}" `
                + 'could not be stored locally; source bytes were retained for Retry.',
            );
            error.code = 'PHOTO_LOCAL_ORIGINAL_DURABILITY_REQUIRED';
            error.retryable = true;
            throw error;
        }
        return cloneWithoutUndefined({
            ...photo,
            data: undefined,
            dataUrl: undefined,
            originalData: undefined,
            aiMarkupData: undefined,
            _localBlobOnly: photo.blobId ? true : undefined,
            _localMemoryOnly: photo.blobId ? undefined : true,
            _cloudUploadBlocked: true,
            _cloudUploadBlockedReason: 'raw_original_cloud_upload_forbidden',
        });
    }

    if (photo._skipBlobMigration || photo.fromVideo) {
        if (photo.s3Key) {
            const keepAiMarkupInline = photo.aiAnnotated && !photo.manualEditedAt && isDataUrl(photo.aiMarkupData);
            const keepLegacyOriginal = photo.aiAnnotated && !photo.manualEditedAt && isDataUrl(photo.originalData);
            return cloneWithoutUndefined({
                ...photo,
                data: undefined,
                dataUrl: undefined,
                aiMarkupData: keepAiMarkupInline ? photo.aiMarkupData : undefined,
                originalData: keepLegacyOriginal ? photo.originalData : undefined,
                _localBlobOnly: undefined,
                _cloudUploadPending: undefined,
                _cloudUploadPendingReason: undefined,
                _forcePhotoUpload: undefined,
            });
        }
        if (photo.blobId && !photo.s3Key && !photo.data) {
            return { ...photo, _localBlobOnly: true };
        }
        return photo;
    }

    if (photo.s3Key) {
        const keepAiMarkupInline = photo.aiAnnotated && !photo.manualEditedAt && isDataUrl(photo.aiMarkupData);
        const keepLegacyOriginal = photo.aiAnnotated && !photo.manualEditedAt && isDataUrl(photo.originalData);
        return cloneWithoutUndefined({
            ...photo,
            data: undefined,
            dataUrl: undefined,
            aiMarkupData: keepAiMarkupInline ? photo.aiMarkupData : undefined,
            originalData: keepLegacyOriginal ? photo.originalData : undefined,
            _localBlobOnly: undefined,
            _cloudUploadPending: undefined,
            _cloudUploadPendingReason: undefined,
            _forcePhotoUpload: undefined,
        });
    }

    if (photo.blobId && !photo.s3Key) {
        // v5.41.3 hot-fix (2026-05-24): when blob backing exists, ALWAYS
        // strip inline data — including data: URLs that earlier preserved.
        // The blob in IndexedDB is the durable source; resolvePhotoDataUrl
        // falls back to it automatically (photo-auto-assign.js:820-831). The
        // old branch that kept the data URL when present was the photo half
        // of the 413 Payload Too Large class-of-bug — multi-MB base64 photos
        // rode into JSONB on every save when S3 upload was unavailable.
        return cloneWithoutUndefined({
            ...photo,
            data: undefined,
            dataUrl: undefined,
            _localBlobOnly: true,
        });
    }

    return photo;
}

/**
 * DEC-COUNCIL-007 / Candidate C6 Slice 4 wrapping: runs the strip-inline-data
 * invariant guard on the result before returning.
 */
export function normalisePhotoForPersistence(photo) {
    const result = _normalisePhotoForPersistenceImpl(photo);
    _assertNoStrandedInlineData(result);
    return result;
}

export async function makePhotoPortableForPersistence(photo, projectId, catalog = null, opts = {}) {
    if (!isObjectPhoto(photo)) return photo;
    const merged = mergePortablePhotoFields(photo, findCatalogPhoto(photo, catalog));
    const uploaded = await uploadPhotoToS3IfPossible(merged, projectId, opts);

    // CZ-C (2026-05-15): pre-strip validation. When the photo carries s3Key
    // (either newly uploaded or pre-existing) AND has inline data we'd be
    // about to discard, verify the backend resource is reachable. Skip the
    // strip on failure — keep inline data + tag with `_pendingValidation`.
    if (_preStripValidationKillSwitch() || !uploaded.s3Key) {
        return normalisePhotoForPersistence(uploaded);
    }
    const wouldStripInline = isDataUrl(uploaded.data) || isDataUrl(uploaded.dataUrl);
    if (!wouldStripInline) {
        return normalisePhotoForPersistence(uploaded);
    }
    const verdict = await _validateBackendResource(uploaded);
    if (verdict === 'fail') {
        // Backend resource unreachable — preserve inline data so the photo
        // renders even when the backend stays unreachable. Flag for the UI
        // to surface a "needs validation / re-upload" badge.
        return {
            ...uploaded,
            _pendingValidation: true,
            _pendingValidationAt: new Date().toISOString(),
        };
    }
    return normalisePhotoForPersistence(uploaded);
}

export async function makePhotoArrayPortableForPersistence(photos = [], projectId, opts = {}) {
    if (!Array.isArray(photos)) return [];
    const portable = [];
    for (const photo of photos) {
        portable.push(await makePhotoPortableForPersistence(photo, projectId, null, opts));
    }
    return portable;
}

export async function makeScopeImagesPortableForPersistence(scope = [], projectId, catalog = null, opts = {}) {
    if (!Array.isArray(scope)) return scope;
    const nextScope = [];

    for (const item of scope) {
        if (!item || typeof item !== 'object') {
            nextScope.push(item);
            continue;
        }
        if (!Array.isArray(item.images)) {
            nextScope.push(item);
            continue;
        }
        const images = [];
        for (const img of item.images) {
            images.push(await makePhotoPortableForPersistence(img, projectId, catalog, opts));
        }
        nextScope.push({ ...item, images });
    }

    return nextScope;
}

export async function makeQuoteDataPortableForPersistence(quoteData, projectId, catalog = null, opts = {}) {
    if (!quoteData || typeof quoteData !== 'object') return quoteData;
    let next = quoteData;
    if (Array.isArray(quoteData.scope)) {
        const scope = await makeScopeImagesPortableForPersistence(quoteData.scope, projectId, catalog, opts);
        if (scope !== quoteData.scope) next = { ...next, scope };
    }

    const header = quoteData.header;
    if (header && typeof header === 'object' && !Array.isArray(header)
        && Object.prototype.hasOwnProperty.call(header, 'heroImage')) {
        const heroImage = header.heroImage;
        if (typeof heroImage === 'string'
            && (heroImage.startsWith('data:image/') || heroImage.startsWith('blob:'))) {
            const error = new Error(
                'Quote hero image still contains session-only bytes. Materialise it as a project photo before saving.',
            );
            error.code = 'QUOTE_HERO_DURABILITY_REQUIRED';
            error.retryable = true;
            throw error;
        }
        if (heroImage && typeof heroImage === 'object' && !Array.isArray(heroImage)) {
            const catalogPhoto = findCatalogPhoto(heroImage, catalog);
            const merged = mergePortablePhotoFields(heroImage, catalogPhoto);
            const hasInline = isDataUrl(merged.data)
                || isDataUrl(merged.dataUrl)
                || isDataUrl(merged.originalData);
            const hasDurableReference = !!(merged.s3Key || merged.fileMetadataId || merged.blobId);
            if (hasInline && !hasDurableReference) {
                const error = new Error(
                    'Quote hero image has no durable project photo or blob reference.',
                );
                error.code = 'QUOTE_HERO_DURABILITY_REQUIRED';
                error.retryable = true;
                throw error;
            }
            const portableHero = await makePhotoPortableForPersistence(merged, projectId, catalog, opts);
            next = {
                ...next,
                header: { ...header, heroImage: portableHero },
            };
        }
    }
    return next;
}
